import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../firebase";
import {
  FaCamera,
  FaImages,
  FaTimes,
  FaBox,
  FaEye,
  FaEyeSlash,
  FaWeightHanging,
  FaTag,
  FaCircleNotch,
} from "react-icons/fa";
import "./addProducts.scss";

const AddProduct = () => {
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [formData, setFormData] = useState({
    sku: `VJ-${Date.now().toString().slice(-6)}`,
    name: "",
    category: "",
    subCategory: "",
    stockStatus: "In Stock",
    visibility: "Public",
    quantity: 1,
    weight: "",
    size: "",
    carat: "22K",
    description: "",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      const catSnapshot = await getDocs(collection(db, "categories"));
      const catData = catSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(catData);
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const selectedCat = categories.find(
      (cat) => cat.name === formData.category,
    );
    setSubCategories(selectedCat ? selectedCat.subCategories || [] : []);
    setFormData((prev) => ({ ...prev, subCategory: "" }));
  }, [formData.category, categories]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleToggleVisibility = () => {
    setFormData((prev) => ({
      ...prev,
      visibility: prev.visibility === "Public" ? "Hidden" : "Public",
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newUrls = files.map((f) => URL.createObjectURL(f));
    setImages((prev) => [...prev, ...files]);
    setPreviewUrls((prev) => [...prev, ...newUrls]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setPreviewUrls(previewUrls.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (images.length === 0)
      return alert("At least one product photo is required.");
    if (!formData.name || !formData.category)
      return alert("Title and Category are required.");

    setIsSubmitting(true);

    try {
      const imageUrls = await Promise.all(
        images.map(async (img) => {
          const storageRef = ref(
            storage,
            `products/vj_${Date.now()}_${img.name}`,
          );
          await uploadBytes(storageRef, img);
          return await getDownloadURL(storageRef);
        }),
      );

      const payload = {
        ...formData,
        quantity:
          formData.stockStatus === "In Stock" ? parseInt(formData.quantity) : 0,
        images: imageUrls,
        createdAt: serverTimestamp(),
        searchIndex: `${formData.name} ${formData.sku}`.toLowerCase(),
      };

      await addDoc(collection(db, "products"), payload);

      alert("Product successfully added to Secure Vault!");
      setFormData({
        sku: `VJ-${Date.now().toString().slice(-6)}`,
        name: "",
        category: "",
        subCategory: "",
        stockStatus: "In Stock",
        visibility: "Public",
        quantity: 1,
        weight: "",
        size: "",
        carat: "22K",
        description: "",
      });
      setImages([]);
      setPreviewUrls([]);
    } catch (error) {
      console.error(error);
      alert("Failed to upload product.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="v-adm-add-app-shell fade-in">
      {/* 1. APP HEADER */}
      <div className="v-adm-add-header slide-down">
        <div>
          <h2>Add New Product</h2>
          <p>Instantly upload inventory to the master catalog.</p>
        </div>
      </div>

      {/* 2. SCROLLABLE BODY */}
      <div className="v-adm-add-scroll-body">
        <div className="v-adm-add-grid-layout scale-in">
          {/* LEFT: Media & Core */}
          <div className="v-adm-add-column">
            {/* 🔥 UPGRADED MEDIA UPLOAD ZONE 🔥 */}
            <div className="v-adm-add-card media-card">
              <div className="card-header-flex">
                <h3 className="v-adm-add-card-title">
                  <FaCamera className="title-icon" /> Product Media
                </h3>
                <span className="media-counter">{images.length}/5 Added</span>
              </div>

              <div className="v-adm-split-media-zone">
                {/* Option 1: Native Advanced Camera */}
                <div
                  className="media-action-btn camera-action"
                  onClick={() => cameraInputRef.current.click()}
                >
                  <div className="icon-circle bg-blue">
                    <FaCamera />
                  </div>
                  <span className="action-title">Open Camera</span>
                  <span className="action-sub">Full resolution & ratios</span>
                </div>

                <div className="media-divider"></div>

                {/* Option 2: Gallery Picker */}
                <div
                  className="media-action-btn gallery-action"
                  onClick={() => galleryInputRef.current.click()}
                >
                  <div className="icon-circle bg-emerald">
                    <FaImages />
                  </div>
                  <span className="action-title">Browse Gallery</span>
                  <span className="action-sub">Select multiple</span>
                </div>
              </div>

              {/* Hidden Inputs */}
              <input
                type="file"
                accept="image/*"
                capture="environment" // Forces camera directly
                ref={cameraInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
              <input
                type="file"
                multiple
                accept="image/*" // Opens gallery/picker
                ref={galleryInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />

              {previewUrls.length > 0 && (
                <div className="v-adm-add-gallery">
                  {previewUrls.map((url, i) => (
                    <div key={i} className="v-adm-add-thumb pop-in">
                      <img src={url} alt="Preview" />
                      <button type="button" onClick={() => removeImage(i)}>
                        <FaTimes />
                      </button>
                      {i === 0 && <span className="cover-badge">Cover</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* CORE DETAILS */}
            <div className="v-adm-add-card">
              <h3 className="v-adm-add-card-title">
                <FaTag className="title-icon" /> Product Identification
              </h3>
              <div className="v-adm-add-input-group">
                <label>Product Title</label>
                <input
                  type="text"
                  name="name"
                  placeholder="e.g. Bridal Choker Set"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="v-adm-add-input-row">
                <div className="v-adm-add-input-group">
                  <label>Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select...
                    </option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="v-adm-add-input-group">
                  <label>Sub Category</label>
                  <select
                    name="subCategory"
                    value={formData.subCategory}
                    onChange={handleChange}
                    disabled={!subCategories.length}
                  >
                    <option value="">Select...</option>
                    {subCategories.map((sub, i) => (
                      <option key={i} value={sub}>
                        {sub}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Controls & Specs */}
          <div className="v-adm-add-column">
            {/* POWERFUL CONTROL CENTER */}
            <div className="v-adm-add-card v-adm-add-control-center">
              <h3 className="v-adm-add-card-title">
                <FaBox className="title-icon" /> Inventory Controls
              </h3>

              {/* Visibility Switch */}
              <div className="v-adm-add-visibility-box">
                <div>
                  <h4>Retailer Visibility</h4>
                  <p
                    className={
                      formData.visibility === "Public"
                        ? "text-emerald"
                        : "text-muted"
                    }
                  >
                    {formData.visibility === "Public"
                      ? "Visible to all retailers in catalog."
                      : "Hidden. Internal only."}
                  </p>
                </div>
                <button
                  type="button"
                  className={`v-adm-add-switch ${formData.visibility === "Public" ? "active" : ""}`}
                  onClick={handleToggleVisibility}
                >
                  <div className="v-adm-add-switch-knob">
                    {formData.visibility === "Public" ? (
                      <FaEye className="icon-pub" />
                    ) : (
                      <FaEyeSlash className="icon-hid" />
                    )}
                  </div>
                </button>
              </div>

              <div className="v-adm-add-input-row mt-16">
                <div className="v-adm-add-input-group">
                  <label>Stock Status</label>
                  <select
                    name="stockStatus"
                    value={formData.stockStatus}
                    onChange={handleChange}
                  >
                    <option value="In Stock">🟢 In Stock (Ready)</option>
                    <option value="Made to Order">
                      📖 Made to Order (Catalog)
                    </option>
                    <option value="Sold Out">🔴 Sold Out</option>
                  </select>
                </div>
                {formData.stockStatus === "In Stock" && (
                  <div className="v-adm-add-input-group">
                    <label>Quantity Available</label>
                    <input
                      type="number"
                      name="quantity"
                      min="1"
                      value={formData.quantity}
                      onChange={handleChange}
                    />
                  </div>
                )}
              </div>
              <div className="v-adm-add-input-group">
                <label>SKU (Auto-Generated)</label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  className="sku-input"
                />
              </div>
            </div>

            {/* SPECS */}
            <div className="v-adm-add-card">
              <h3 className="v-adm-add-card-title">
                <FaWeightHanging className="title-icon" /> Physical Specs
              </h3>
              <div className="v-adm-add-input-row">
                <div className="v-adm-add-input-group">
                  <label>Est. Weight (g)</label>
                  <input
                    type="number"
                    name="weight"
                    step="0.01"
                    placeholder="e.g. 15.5"
                    value={formData.weight}
                    onChange={handleChange}
                  />
                </div>
                <div className="v-adm-add-input-group">
                  <label>Size / Length</label>
                  <input
                    type="text"
                    name="size"
                    placeholder="e.g. 18 inch"
                    value={formData.size}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="v-adm-add-input-group">
                <label>Purity / Carat</label>
                <select
                  name="carat"
                  value={formData.carat}
                  onChange={handleChange}
                >
                  <option value="24K">24K</option>
                  <option value="22K">22K</option>
                  <option value="18K">18K</option>
                </select>
              </div>
              <div className="v-adm-add-input-group">
                <label>Internal Description</label>
                <textarea
                  name="description"
                  rows="3"
                  placeholder="Additional details, stones, or maker notes..."
                  value={formData.description}
                  onChange={handleChange}
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. MOBILE STICKY BOTTOM BAR */}
      <div className="v-adm-add-sticky-bottom slide-up">
        <button
          className="v-adm-add-btn-publish"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <FaCircleNotch className="v-adm-add-spinner" /> Uploading to
              Vault...
            </>
          ) : (
            "Publish Product"
          )}
        </button>
      </div>
    </div>
  );
};

export default AddProduct;
